import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { CombinedReadonlyDirective } from '../../../../../../../directives/combined-readonly.directive';
import { ActivityCarRental, ActivityEditDialogData } from '../../../../../../../models';
import { AppStore } from '../../../../../../../store/store';
import { selectTripEditData } from '../../../../../../../store/trip/trip-edit/selectors-trip-edit';
import { InputDateComponent } from '../../../../../../shared/input-date/input-date.component';
import { InputLinkComponent } from '../../../../../../shared/input-link/input-link.component';
import { InputMonetaryAmountComponent } from '../../../../../../shared/input-monetary-amount/input-monetary-amount.component';
import { InputNumberComponent } from '../../../../../../shared/input-number/input-number.component';
import { InputPhoneComponent } from '../../../../../../shared/input-phone/input-phone.component';
import { InputTimeComponent } from '../../../../../../shared/input-time/input-time.component';
import { CAR_RENTAL_DISCLAIMER_TEXT, CAR_RENTAL_WHAT_INCLUDED_TEXT, convertDatesToStrings, fromStringToDate } from '../../../../../../shared/utils';

@Component({
  selector: 'app-car-rental-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    DragDropModule,
    TranslateModule,
    InputDateComponent,
    InputNumberComponent,
    InputTimeComponent,
    InputPhoneComponent,
    InputMonetaryAmountComponent,
    InputDateComponent,
    InputLinkComponent,
    CombinedReadonlyDirective
  ],
  templateUrl: './car-rental-edit-dialog.component.html'
})
export class CarRentalEditDialogComponent implements OnInit {
  form!: FormGroup;

  startDate!: Date;
  endDate!: Date;
  advancePurchaseDate?: Date;

  constructor(
    private dialogRef: MatDialogRef<CarRentalEditDialogComponent>,
    private store: Store<AppStore>,

    private cdr: ChangeDetectorRef,

    @Inject(MAT_DIALOG_DATA) public data: ActivityEditDialogData
  ) {

    // get the start and end dates from the trip-edit-context store
    this.store.select(selectTripEditData).subscribe(tripData => {
      this.startDate = fromStringToDate(tripData!.startDate) || new Date();
      this.endDate = fromStringToDate(tripData!.endDate) || new Date();
      // la data di acquisto anticipato ha come limite massimo la start date
      // e viene prevalorizzata 15 gg prima di essa
      this.advancePurchaseDate = new Date(this.startDate!);
      this.advancePurchaseDate.setDate(this.advancePurchaseDate.getDate() - 15);
    });
  }

  ngOnInit(): void {

    this.initForm();

    this.form.get('advancePurchaseDate')?.setValue(this.advancePurchaseDate);

    // edit an existing car-rental
    if (this.data.activityData) {
      this.fillFormFields(this.data.activityData as ActivityCarRental);
    }

    // advancedPurchaseDate has been changed so i need manually to call the change detection
    this.cdr.detectChanges();

  }

  initForm(): void {


    this.form = new FormGroup({
      checkIn: new FormControl('', [Validators.required]),
      checkOut: new FormControl('', [Validators.required]),
      startTime: new FormControl('', [Validators.required]),
      endTime: new FormControl('', [Validators.required]),
      vectorName: new FormControl('', [Validators.required]),
      carType: new FormControl('', [Validators.required]),
      transmissionType: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required]),
      meetingPoint: new FormControl('', [Validators.required]),
      depositAmount: new FormControl(0, [Validators.required, Validators.min(0)]),
      returnPoint: new FormControl(''),
      baggageNumber: new FormControl(0, [Validators.required, Validators.min(0)]),
      totalPrice: new FormControl(0, [Validators.required, Validators.min(0)]),
      whatIsIncluded: new FormControl(CAR_RENTAL_WHAT_INCLUDED_TEXT),
      advancePurchaseDate: new FormControl(this.advancePurchaseDate ?? '', [Validators.required]),
      notes: new FormControl(''),
      disclaimer: new FormControl(CAR_RENTAL_DISCLAIMER_TEXT),
      type: new FormControl('car-rental'),
      name: new FormControl('Car Rental')
    });
  }

  private fillFormFields(data: ActivityCarRental): void {
    this.form.patchValue({
      checkIn: data.checkIn,
      checkOut: data.checkOut,
      startTime: data.startTime,
      endTime: data.endTime,
      vectorName: data.vectorName,
      carType: data.carType,
      transmissionType: data.transmissionType,
      phone: data.phone,
      meetingPoint: data.meetingPoint,
      depositAmount: data.depositAmount,
      returnPoint: data.returnPoint,
      baggageNumber: data.baggageNumber,
      totalPrice: data.totalPrice,
      advancePurchaseDate: data.advancePurchaseDate,
      notes: data.notes,
      whatIsIncluded: data.whatIsIncluded,
      disclaimer: data.disclaimer
    });
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // In this case we dont need a support local carRental object because
    // the returned datas from this dialog exactly matches the carRental edit form
    const formValueWithStringDates = convertDatesToStrings(this.form.getRawValue());
    this.dialogRef.close(formValueWithStringDates);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}